import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { type User } from '@/types/user';
import { useUserCompanies, useUpdateUserCompanies, type UserCompany } from '@/hooks/useUsersData';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface ManageUserCompaniesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

export function ManageUserCompaniesDialog({
  open,
  onOpenChange,
  user,
}: ManageUserCompaniesDialogProps) {
  const { data: companies, isLoading: isLoadingCompanies, error: companiesError } = useUserCompanies(user?.id ?? null);
  const updateUserCompaniesMutation = useUpdateUserCompanies();
  
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (companies) {
      setSelectedCompanyIds(companies.filter(c => c.isLinked).map(c => c.id));
    }
  }, [companies]);

  const handleCompanyToggle = (companyId: number, checked: boolean) => {
    if (checked) {
      setSelectedCompanyIds(prev => [...prev, companyId]);
    } else {
      setSelectedCompanyIds(prev => prev.filter(id => id !== companyId));
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      await updateUserCompaniesMutation.mutateAsync({
        userId: user.id,
        companyIds: selectedCompanyIds,
      });
      toast.success('User companies updated successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating user companies:', error);
      toast.error('Failed to update user companies. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Companies</DialogTitle>
          <DialogDescription>
            Select which companies <strong>{user.firstName} {user.lastName}</strong> should have access to.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          {isLoadingCompanies ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading companies...</span>
            </div>
          ) : companiesError ? (
            <div className="text-sm text-destructive py-4">
              Failed to load companies. Please try again.
            </div>
          ) : companies && companies.length > 0 ? (
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-3">
                <Label>Available Companies</Label>
                {companies.map((company: UserCompany) => (
                  <div key={company.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent">
                    <Checkbox
                      id={`company-${company.id}`}
                      checked={selectedCompanyIds.includes(company.id)}
                      onCheckedChange={(checked) => handleCompanyToggle(company.id, checked as boolean)}
                    />
                    <div className="flex-1">
                      <Label htmlFor={`company-${company.id}`} className="font-medium cursor-pointer">
                        {company.name}
                      </Label>
                      {company.code && (
                        <p className="text-sm text-muted-foreground">
                          Code: {company.code}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-sm text-muted-foreground py-4">
              No companies available for this organisation.
            </div>
          )}
        </div>
        <DialogFooter>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || isLoadingCompanies}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
